import {
  Box,
  BoxProps,
  ColorOptions,
  Icon,
  ModBox,
  SpacerNew,
  Text,
  TextInputNew,
  TextInputNewProps
} from '@jfs/ui-new'
import { FC, useState } from 'react'
import ReactDOM from 'react-dom'
import { FlatList, LayoutChangeEvent, useWindowDimensions } from 'react-native'

export const Types = {
  LOCATION_SEARCH: 'Location',
  DEFAULT: 'Default',
}

export interface AutoCompleteDropdownProps {
  data: any
  label?: string
  type: keyof typeof Types | Array<keyof typeof Types>
  initialValue?: string
  onClose?: () => void
  autoFocus?: true
  onItemSelected?: (item: any) => void
  onTextChanged?: (value: string) => void
  setFieldValue?: (field: string, value: string | number) => void
  fieldName?: string
  error?: string
  touched?: boolean
}

export const AutoCompleteDropdown: FC<AutoCompleteDropdownProps> = (props: AutoCompleteDropdownProps) => {
  const { data, label, type, onItemSelected, onTextChanged, setFieldValue, fieldName, error, touched } = props

  const [searchText, setSearchText] = useState('')
  const [showDropdown, setShowDropDown] = useState(false)
  const { width, height } = useWindowDimensions()

  const [layout, setLayout] = useState({ left: width / 2 - 142, top: height / 2 + 88 })
  const getListData = () => {
    if (type === 'DEFAULT') {
      const searchfn = (item: any) => {
        const search = searchText?.toLowerCase()
        const itemText = item?.text?.toLowerCase() ?? ''
        return itemText?.includes(search)
      }
      const listData = data?.filter(searchfn)
      return searchText?.length > 0 && listData?.length === 0 ? [{ id: data?.length + 1, text: '+ Add New' }] : listData
    }
  }

  const onValueChanged = (textValue: string) => {
    setSearchText(textValue)
    if (onTextChanged) {
      onTextChanged(textValue)
    }
    if (fieldName && setFieldValue) {
      setFieldValue(fieldName, textValue)
    }
  }

  const handleOnItemPressed = (item: any) => {
    if (item?.text?.includes('+') || item?.value?.includes('+')) {
      onItemSelected && onItemSelected(searchText)
      if (fieldName && setFieldValue) {
        setFieldValue(fieldName, searchText)
      }
      setSearchText(searchText)
    } else {
      onItemSelected && onItemSelected(item)
      if (fieldName && setFieldValue) {
        setFieldValue(fieldName, item?.text ?? item?.value)
      }
    }
    setShowDropDown(false)
  }

  const renderItem = ({ item }: { item: any }) => {
    return (
      <Box
        w={'100%'}
        h={32}
        alignItems={'center'}
        padding={8}
        flexDirection="row"
        borderBottomColor="GRAY_BORDER"
        borderBottomWidth={1}
        underlayColor="TRANSPARENT"
        backgroundColor={'WHITE'}
        testID={'setAutoComplete-' + item?.text?.replaceAll('', '')}
        onPress={() => {
          if (item) {
            setSearchText(item?.text ? item?.text : item?.value)
            if (handleOnItemPressed) {
              handleOnItemPressed(item)
            }
          }
        }}>
        {item?.image && (
          <>
            <Icon id={item?.image} size="FONT_SIZE_LARGE" type="secondary" />
            <SpacerNew direction="horizontal" size="MEDIUM" />
          </>
        )}
        <Text fontSize={12} fontFamily="jiotype-medium" fontWeight="normal" color="BLACK">
          {item?.text}
        </Text>
      </Box>
    )
  }

  const onFocusHandler = () => {
    setShowDropDown((prev) => !prev)
  }

  const onHandleLayout = (event: LayoutChangeEvent) => {
    console.log('LayoutChangeEvent: ', event)
    setLayout({ left: event?.nativeEvent?.layout?.left, top: event?.nativeEvent?.layout?.top + 66 })
  }

  if (type === 'DEFAULT') {
    return (
      <Box {...styles.boxContainer} onLayout={onHandleLayout}>
        <TextInputNew
          {...styles.textinputStyle(true)}
          value={searchText}
          onChange={onValueChanged}
          placeholder={!showDropdown ? 'Select' : ''}
          color={'TEXT_PRIMARY'}
          onFocus={onFocusHandler}
          label={label}
          paddingHorizontal={8}
          rightComponent={!showDropdown ? <Icon id="ARROW_DOWN" color="BLACK_75" /> : undefined}
        />
        {showDropdown &&
          ReactDOM.createPortal(
            <ModBox
              backgroundColor={'WHITE'}
              position={'absolute'}
              top={layout?.top}
              left={layout?.left}
              borderRadius={4}
              w={150}
              borderWidth={1}
              borderColor={'BORDER_COLOR'}
              shadowColor={'SHADOW_LIGHT_GREY'}
              shadowType={'shadowPrimary'}
              shadowRadius={4}
              zIndex={999999}>
              <FlatList
                keyExtractor={({ id }) => id}
                bounces={false}
                overScrollMode="never"
                renderItem={renderItem}
                data={getListData()}
              />
            </ModBox>,
            document.body,
          )}
        {touched && !!error && (
          <>
            <SpacerNew size={2} />
            <Text testID={'textInputErrorId'} color="ERROR" type="h4Bold">
              {error}
            </Text>
          </>
        )}
      </Box>
    )
  }
}

const styles = {
  boxContainer: {
    height: '100%',
    width: '100%',
    borderColor: '#d1d3e2' as ColorOptions,
    borderRadius: 4,
  } as BoxProps,
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  } as BoxProps,
  textinputStyle: (isEditMode: boolean) =>
  ({
    type: 'body12',
    borderColor: isEditMode ? ('#d1d3e2' as ColorOptions) : 'WHITE',
    labelPaddingBottom: 10,
    borderRadius: isEditMode ? 4 : 0,
    labelFontFamily: 'jiotype-bold',
    fontFamily: 'jiotype-medium',
    labelColor: '#757575' as ColorOptions,
    labelFontSize: 12,
    fontSize: 12,
    selectionColor: isEditMode ? 'BLUE' : 'TRANSPARENT',
    paddingVertical: 9,
    padddingHorizontal: 16,
    color: 'BLACK_21' as ColorOptions,
    onFocusBorderWidth: isEditMode ? 1 : 0,
  } as unknown as TextInputNewProps),
}
